<template>
    <div>
        <pendo-multiselect
            class="verification-status"
            :options="options"
            :value="valueObject"
            :disabled="loading"
            value-key="state"
            :append-to-body="false"
            @select="onSelect"
            @open="onOpen">
            <template #trigger>
                <pendo-status-trigger
                    v-pendo-tooltip="{
                        arrow: true,
                        multiline: true,
                        content: tooltipContent,
                    }"
                    :aria-label="canVerify ? $t('verificationStatus.Select verification status') : $t('verificationStatus.Verification status')"
                    data-cy="status-trigger"
                    :size="size"
                    :status-color-map="STATUS_COLOR_MAP"
                    :read-only="readOnly || availableOptions.length < 2" />
            </template>
            <template #option="{ option }">
                <pendo-status-option
                    v-if="option.icon"
                    :status-color-map="STATUS_COLOR_MAP"
                    :option="option" />
                <span
                    v-else
                    class="action-option"
                    :style="option.color && { color: option.color }">
                    {{ option.label }}
                </span>
            </template>
        </pendo-multiselect>
        <verification-modal
            :visible="showConfirmModal"
            :action="actionToConfirm"
            :updated-by="updatedBy"
            :updated-at="updatedAt"
            :entity-name="entityName"
            :entity-type="entityType"
            :allow-note="allowNote"
            @confirm="onConfirm"
            @cancel="onCancel" />
    </div>
</template>

<script>
import { PendoMultiselect, PendoStatusTrigger, PendoStatusOption, PendoTooltip } from '@pendo/components';
import VerificationModal from './verification-modal';
import {
    getActionOptions,
    getStateOptions,
    ACTION_STATE_MAP,
    STATUS_COLOR_MAP,
    STATE_ACTION_MAP,
    tooltipContent,
} from './verification-status.utils';

export default {
    name: 'VerificationStatus',
    components: {
        PendoMultiselect,
        PendoStatusTrigger,
        PendoStatusOption,
        VerificationModal,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        value: {
            type: String,
            required: true,
            validator: (value) => ['verified', 'unverified', 'requested', 'rejected'].includes(value),
        },
        canVerify: {
            type: Boolean,
            default: false,
        },
        note: {
            type: String,
            default: '',
        },
        updatedBy: {
            type: String,
            default: '',
        },
        updatedAt: {
            type: [Number, null],
            default: null,
        },
        entityName: {
            type: String,
            default: 'this entity',
        },
        entityType: {
            type: String,
            default: 'entity',
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        size: {
            type: String,
            default: 'medium',
        },
        allowNote: {
            type: Boolean,
            default: true,
        },
    },
    /**
     * input:  emitted when the verification status is actually changed
     * select: emitted when the user selects an option from the dropdown
     *         even when the status is not changed, or the action is unrelated to the status
     */
    emits: ['input', 'select'],
    data () {
        return {
            actionToConfirm: 'verify',
            showConfirmModal: false,
            options: [],
            STATUS_COLOR_MAP,
        };
    },
    computed: {
        valueObject () {
            return getStateOptions()[this.value];
        },
        availableOptions () {
            const stateOptions = getStateOptions();
            const actionOptions = getActionOptions();

            switch (this.value) {
                case 'verified':
                    return [this.canVerify && stateOptions.unverified, stateOptions.verified].filter(Boolean);
                case 'unverified':
                    return [
                        stateOptions.unverified,
                        this.canVerify ? stateOptions.verified : { ...actionOptions.request, divided: true },
                    ];
                case 'requested':
                    return this.canVerify
                        ? [
                            stateOptions.requested,
                            { ...actionOptions.accept, divided: true },
                            actionOptions.reject,
                            this.note.length && this.allowNote && actionOptions.view,
                        ].filter(Boolean)
                        : [stateOptions.requested, { ...actionOptions.cancel, divided: true }];
                case 'rejected':
                    return [
                        stateOptions.rejected,
                        stateOptions.unverified,
                        this.canVerify ? stateOptions.verified : { ...actionOptions.request, divided: true },
                    ];
                default:
                    return [];
            }
        },
        tooltipContent () {
            return tooltipContent(
                {
                    state: this.value,
                    lastUpdatedAt: this.updatedAt,
                },
                this.updatedBy,
            );
        },
    },
    methods: {
        onSelect ({ state }) {
            this.$emit('select', state);

            const newValue = ACTION_STATE_MAP[state] || state;

            if (this.value === 'rejected' && newValue === 'requested') {
                this.actionToConfirm = 'rerequest';
                this.showConfirmModal = true;
            } else if (newValue !== this.value) {
                this.actionToConfirm = STATE_ACTION_MAP[state] || state;
                this.showConfirmModal = true;
            }
        },
        onOpen () {
            this.options = this.availableOptions;
        },
        onConfirm (note) {
            this.showConfirmModal = false;

            const newValue = ACTION_STATE_MAP[this.actionToConfirm] || this.actionToConfirm;
            const args = [newValue];
            if (newValue === 'requested' && note) {
                args.push(note);
            }

            this.$emit('input', ...args);
        },
        onCancel () {
            this.showConfirmModal = false;
        },
    },
};
</script>

<style scoped lang="less">
:deep(.pendo-multiselect__trigger svg) {
    fill: none;
}

.action-option {
    line-height: 26px;
}
</style>
